/**
 * Shared Task FHIR constants for Unify Mental Health.
 *
 * Canonical codes and systems that both the Rules Engine (producer) and the
 * Client Portal (consumer) reference to keep Task shape aligned.
 *
 * See: docs/prd/medplum/PRD_RULES_ENGINE.md, docs/prd/Assessments/PRD_ASSESSMENTS_FHIR_MAPPING.md
 *
 * Note: `unifymentalhealth.com` CodeSystem URLs below match existing seeded Task
 * metadata. Medplum-server defines `TASK_TYPE_SYSTEM` under `https://unify.health/fhir/`
 * for new resources — backfill/migrate per object-model PRD before switching imports.
 */

import { TASK_TYPE_SYSTEM as FHIR_TASK_TYPE_SYSTEM_UNIFY_HEALTH } from '@unify/fhir-model/constants';

/** Canonical task-type system from fhir-model (use after data backfill). */
export { FHIR_TASK_TYPE_SYSTEM_UNIFY_HEALTH };

// ---------------------------------------------------------------------------
// Code Systems
// ---------------------------------------------------------------------------

export const RULES_ENGINE_CODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/rules-engine';

export const TASK_TYPE_CODE_SYSTEM =
  'https://unify.health/fhir/task-type';

export const NOTIFICATION_CODE_SYSTEM =
  'https://unify.health/fhir/CodeSystem/notification-email';

// ---------------------------------------------------------------------------
// Task Codes — Rules Engine
// ---------------------------------------------------------------------------

export const RULES_ENGINE_FORM_ASSIGNMENT_CODE = 'rules-engine-form-assignment';
export const MANUAL_RULE_RUN_CODE = 'manual-rule-run';
export const SEND_EMAIL_NOTIFICATION_CODE = 'send-email-notification';

// ---------------------------------------------------------------------------
// Task Codes — Client-facing (portal-visible)
// ---------------------------------------------------------------------------

export const CLIENT_FORM_ASSIGNMENT_CODE = 'client-form-assignment';
/** Scheduled assessment series — created by provider from the Schedule Assessments panel. */
export const CLIENT_ASSESSMENT_ASSIGNMENT_CODE = 'client-assessment-assignment';
/** Provider-administered Take Now assessment — NOT a scheduled series; appears in Assessment Log only. */
export const CLIENT_TAKE_NOW_ASSESSMENT_CODE = 'client-take-now-assessment';
export const CLIENT_SIGNATURE_REQUEST_CODE = 'client-signature-request';
export const CLIENT_DOCUMENT_COMPLETION_CODE = 'client-document-completion';

// ---------------------------------------------------------------------------
// Task meta.tag systems for portal queries
// ---------------------------------------------------------------------------

export const CLIENT_FACING_TASK_TAG_SYSTEM =
  'https://unify.health/fhir/tag/client-facing';
export const CLIENT_FACING_TASK_TAG_CODE = 'client-task';

export const FORM_CATEGORY_TAG_SYSTEM =
  'https://unify.health/fhir/tag/form-category';

// ---------------------------------------------------------------------------
// Open Task statuses recognized by the client portal
// ---------------------------------------------------------------------------

export const PORTAL_OPEN_TASK_STATUSES = [
  'requested',
  'received',
  'accepted',
  'ready',
  'in-progress',
] as const;

export const PORTAL_OPEN_TASK_STATUSES_CSV = PORTAL_OPEN_TASK_STATUSES.join(',');

// ---------------------------------------------------------------------------
// Helper: check if a Task code matches Rules Engine form assignment
// ---------------------------------------------------------------------------

export function isRulesEngineFormAssignmentTask(taskCodings: { system?: string; code?: string }[]): boolean {
  return taskCodings.some(
    (c) =>
      (c.system === RULES_ENGINE_CODE_SYSTEM && c.code === RULES_ENGINE_FORM_ASSIGNMENT_CODE) ||
      (c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_FORM_ASSIGNMENT_CODE),
  );
}

export function isClientFacingTaskByTag(tags: { system?: string; code?: string }[]): boolean {
  return tags.some(
    (t) => t.system === CLIENT_FACING_TASK_TAG_SYSTEM && t.code === CLIENT_FACING_TASK_TAG_CODE,
  );
}
